// @ts-nocheck
import { flatten, isEmpty } from 'lodash';
import React, { createContext, useContext, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { Loader, useQueryFetch } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { ALL_SUMMARY_ITEM_KEYS } from 'sonata-static-storage/lib/samples/statsPreferences.samples';

import { getInitialTimeFrame } from '@/helpers/reporting.helpers';
import { getChangeLogs } from '@/resources/changeLog.resources';
import {
  reportingSetAggregatedStats,
  reportingSetCampaignEvents,
  reportingSetCartography,
  reportingSetChangeLogData,
  reportingSetCountriesWithEvents,
  reportingSetSelectedCampaigns,
  reportingSetStaticMode
} from '@/store/actions/reporting.actions';
import {
  reportingAdvancedFilterSetCampaignIds,
  reportingAdvancedFilterSetCurrency,
  reportingAdvancedFilterSetTimeFrame,
  reportingAdvancedFilterSetTimeZone
} from '@/store/actions/reportingAdvancedFilter.actions';
import { setBaseCurrencyCatalog, setUserRoles } from '@/store/actions/resources.actions';
import { shortcutsConfig } from '@/ts-modules/reporting/Header/Shortcuts/shortcutsConfig';
import { useFetchPaginatedStats } from '@/ts-resources/reporting/summary.resources';
import {
  getBaseCurrencies,
  getCampaignsAdActions,
  getCampaignsData,
  getCartographyDictionary,
  getUserRoles
} from '@/ts-resources/reporting.resources';
import { getAttributionZoneIds, getCountriesFromCampaigns, processChangeLogData } from './helpers';

import type { GeoDictionary } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';
import type { Dispatch } from 'react';
import type { ChangeLogData } from 'sonata-static-storage/lib/common/common.types';
import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';

type DefaultAction = {
  type: string;
};

type InitAction = DefaultAction & {
  payload: {
    campaigns: CampaignItem[];
    user: UserInterface;
    userRoles: UserRoleInterface[];
  };
};

type InitCartographyAction = DefaultAction & {
  payload: GeoDictionary[];
};

type SetStatsAction = DefaultAction & {
  payload: Record<string, number>;
};

type SetCountriesWithEvent = DefaultAction & {
  payload: string[];
};

type SetChangeLogDataAction = DefaultAction & {
  payload: ChangeLogData[];
};

type Action = DefaultAction | InitAction | InitCartographyAction | SetStatsAction | SetCountriesWithEvent;

type State = {
  ads: any[];
  attributionZoneIds: string[];
  cartography: Partial<GeoDictionary>[];
  changeLogData: ChangeLogData[];
  countriesWithEvents: string[];
  currentCurrency: string;
  filters: Record<string, any>;
  sectionsVisibility: Record<string, boolean>;
  selectedCampaigns: CampaignItem[];
  selectedCampaignsIds: string[];
  showGTotal: boolean;
  staticMode: boolean;
  stats: Record<string, number>;
  timeFrame: Record<string, any>;
  timezone: string;
  updateReduxStore: boolean;
  user: UserInterface;
  userRoles: UserRoleInterface[];
  zones: any[];
};

type Props = {
  campaignIds: string[];
  children: React.ReactNode;
  currency: string;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

type ReportingValue = {
  dispatch: Dispatch<Action>;
  reporting: State;
} | null;

const ReportingContext = createContext<ReportingValue>(null);

export const useReporting = () => {
  const context = useContext(ReportingContext);
  if (!context) {
    throw new Error('useReporting cannot be rendered outside the Reporting Provider');
  }
  return context;
};

const ACTIONS = {
  INIT_REPORTING: 'INIT_REPORTING',
  INIT_REPORTING_CARTOGRAPHY: 'INIT_REPORTING_CARTOGRAPHY',
  SET_CHANGE_LOG_DATA: 'SET_CHANGE_LOG_DATA',
  SET_COUNTRIES_WITH_EVENTS: 'SET_COUNTRIES_WITH_EVENTS',
  SET_STATS: 'SET_STATS',
  UPDATE_REDUX_STORE: 'UPDATE_REDUX_STORE'
};

const initialState: State = {
  ads: [],
  attributionZoneIds: [],
  cartography: [],
  changeLogData: [],
  countriesWithEvents: [],
  currentCurrency: 'EUR',
  filters: {},
  sectionsVisibility: shortcutsConfig.reduce(
    (acc, shortcutConfig) => ({
      ...acc,
      [shortcutConfig.id]: true
    }),
    {}
  ),
  selectedCampaigns: [],
  selectedCampaignsIds: [],
  showGTotal: false,
  staticMode: false,
  stats: {},
  timeFrame: {},
  timezone: '',
  updateReduxStore: false,
  user: {} as UserInterface,
  userRoles: [],
  zones: []
};

const reportingReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ACTIONS.INIT_REPORTING: {
      const {
        payload: { campaigns, user, userRoles }
      } = action as InitAction;
      const isStaticMode = campaigns.filter(campaign => campaign.fixStaticReports).length > 0;

      return {
        ...state,
        ads: flatten(campaigns.map(c => c.ads.map((ad: AdInterface) => ad))),
        attributionZoneIds: getAttributionZoneIds(flatten(campaigns.map(c => c.zones))),
        countriesWithEvents: isStaticMode ? getCountriesFromCampaigns(campaigns) : ([] as string[]),
        selectedCampaigns: campaigns,
        selectedCampaignsIds: campaigns.map(c => c.id),
        showGTotal:
          user.billing.method !== 'prepaid' && checkUserResourcePermissions('reportingGTotal', user, userRoles),
        staticMode: isStaticMode,
        timeFrame: getInitialTimeFrame(campaigns),
        timezone: campaigns[0].timezone,
        updateReduxStore: true,
        user,
        userRoles,
        zones: flatten(campaigns.map(c => c.zones))
      };
    }
    case ACTIONS.INIT_REPORTING_CARTOGRAPHY: {
      const { payload: geoDictionary } = action as InitCartographyAction;
      return {
        ...state,
        cartography: geoDictionary
      };
    }
    case ACTIONS.UPDATE_REDUX_STORE:
      return {
        ...state,
        updateReduxStore: false
      };
    case ACTIONS.SET_STATS: {
      const { payload: stats } = action as SetStatsAction;
      return {
        ...state,
        stats
      };
    }
    case ACTIONS.SET_COUNTRIES_WITH_EVENTS: {
      const { payload: countriesWithEvents } = action as SetCountriesWithEvent;
      return {
        ...state,
        countriesWithEvents
      };
    }
    case ACTIONS.SET_CHANGE_LOG_DATA: {
      const { payload: changeLogData } = action as SetChangeLogDataAction;
      return {
        ...state,
        changeLogData
      };
    }
    default:
      throw new Error(`Unknown action: ${action.type}`);
  }
};

export function ReportingProvider({ campaignIds, children, currency, user, userRoles }: Props) {
  const reduxDispatch = useDispatch();
  const [reporting, dispatch] = useReducer(reportingReducer, initialState);
  const isStaticMode = reporting.staticMode;

  const updateReduxStore = () => {
    const { selectedCampaigns } = reporting;
    reduxDispatch(reportingAdvancedFilterSetTimeZone(selectedCampaigns[0].timezone, 'general'));
    reduxDispatch(reportingSetStaticMode(reporting.staticMode));
    reduxDispatch(reportingSetSelectedCampaigns(selectedCampaigns));
    reduxDispatch(reportingAdvancedFilterSetCurrency(currency, 'general'));
    reduxDispatch(reportingAdvancedFilterSetCampaignIds(reporting.selectedCampaignsIds));
    reduxDispatch(reportingAdvancedFilterSetTimeFrame(reporting.timeFrame, 'general'));
  };

  const initToken = {};
  const { isLoading: isInitLoading } = useQueryFetch(['reporting', campaignIds.join(',')], async () => {
    const campaigns = await getCampaignsData(campaignIds, user.id!, initToken);
    dispatch({
      payload: {
        campaigns,
        user,
        userRoles
      },
      type: ACTIONS.INIT_REPORTING
    });
  });

  const campaignsCountries: string[] = flatten([
    ...new Set(flatten(reporting.selectedCampaigns || []).map(sC => sC.countries))
  ]);
  const cartographyToken = {};
  useQueryFetch(
    ['reporting', 'cartography', campaignsCountries.join()],
    async () => {
      const geoDictionary = await getCartographyDictionary(campaignsCountries, cartographyToken);
      dispatch({
        payload: geoDictionary,
        type: ACTIONS.INIT_REPORTING_CARTOGRAPHY
      });
      reduxDispatch(reportingSetCartography(geoDictionary));
    },
    {
      enabled: reporting.selectedCampaigns?.length > 0
    }
  );

  if (reporting.updateReduxStore) {
    updateReduxStore();
    dispatch({
      type: ACTIONS.UPDATE_REDUX_STORE
    });
  }

  useQueryFetch(['reporting', 'baseCurrencies'], async () => {
    const baseCurrencies = await getBaseCurrencies();
    reduxDispatch(setBaseCurrencyCatalog(baseCurrencies));
  });

  useQueryFetch(['reporting', 'userRoles'], async () => {
    const obtainedUserRoles = await getUserRoles();
    reduxDispatch(setUserRoles(obtainedUserRoles));
  });

  useQueryFetch(
    ['reporting', 'campaignActions', ...reporting.selectedCampaignsIds],
    async () => {
      const campaignsActions = await getCampaignsAdActions(reporting.selectedCampaignsIds, initToken);
      reduxDispatch(reportingSetCampaignEvents(campaignsActions));
    },
    {
      enabled: reporting.selectedCampaignsIds.length > 0
    }
  );

  const paginatedStatsToken = {};
  useFetchPaginatedStats(
    reporting.selectedCampaigns[0]?.user,
    reporting.selectedCampaignsIds,
    {
      groupOptions: [],
      outputMetrics: ALL_SUMMARY_ITEM_KEYS
    },
    {
      currencyOptions: {
        currency: reporting.currentCurrency
      }
    },
    paginatedStatsToken,
    {
      enabled: !isInitLoading,
      retry: 0
    },
    stats => {
      reduxDispatch(reportingSetAggregatedStats(stats.metadata.totalResults));
      dispatch({
        payload: stats.metadata.totalResults,
        type: ACTIONS.SET_STATS
      });
    }
  );

  const countriesFromCampaignsToken = {};
  useFetchPaginatedStats(
    reporting.selectedCampaigns[0]?.user,
    reporting.selectedCampaignsIds,
    {
      dateInterval: {
        ...reporting.timeFrame,
        timezone: reporting.timezone
      },
      filters: {
        attrType: ['inStore', 'N/A']
      },
      groupOptions: [
        {
          field: 'cartoPyramid.level0',
          from: 0,
          others: false,
          size: 50
        }
      ]
    },
    {
      currencyOptions: {
        currency: reporting.currentCurrency
      }
    },
    countriesFromCampaignsToken,
    {
      enabled: !isStaticMode && !isInitLoading,
      retry: 0
    },
    statsByCountry => {
      let countries = [
        ...new Set(
          statsByCountry.results
            .map(itemData => itemData.cartography.name)
            .filter(countryCode => /^[A-Z]{3}$/.test(countryCode))
        )
      ];
      if (isEmpty(countries)) {
        countries = getCountriesFromCampaigns(reporting.selectedCampaigns);
      }
      dispatch({
        payload: countries,
        type: ACTIONS.SET_COUNTRIES_WITH_EVENTS
      });
      reduxDispatch(reportingSetCountriesWithEvents(countries));
    }
  );

  useQueryFetch(
    ['changeLog', campaignIds.join(',')],
    async () => {
      const res = await getChangeLogs(campaignIds, user.id!);
      const changeLogData = processChangeLogData(res.items, user);
      reduxDispatch(reportingSetChangeLogData(changeLogData));
      dispatch({
        payload: changeLogData,
        type: ACTIONS.SET_CHANGE_LOG_DATA
      });
    },
    {
      enabled: Boolean(reporting.selectedCampaigns.length)
    }
  );

  if (isInitLoading) {
    return (
      <Loader backgroundColor="var(--color-sonata-secondary-white)" showBackground type="landing" variant="default" />
    );
  }

  return (
    <ReportingContext.Provider
      value={{
        dispatch,
        reporting
      }}
    >
      {children}
    </ReportingContext.Provider>
  );
}
